import { AnimationEvent } from '@angular/animations';
import { BasePortalOutlet, CdkPortalOutlet, ComponentPortal, Portal, TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Directive,
  EmbeddedViewRef,
  EventEmitter,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { dialogContainer } from 'src/app/pages/dispatch/animations';
import { ArkikDialogConfig } from './arkik-dialog.service';

@Directive()
export abstract class _ArkikDialogContainerBase extends BasePortalOutlet {
  @ViewChild(CdkPortalOutlet, { static: true }) _portalOutlet: CdkPortalOutlet;

  /** Emits when an animation state changes. */
  _animationStateChanged = new EventEmitter<ArkikAnimationEvent>();

  /** Emits when close button clicked */
  _dialogClose = new EventEmitter();

  constructor(
    public _config: ArkikDialogConfig,
    protected _changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  /** Starts the dialog exit animation. */
  abstract _startExitAnimation(): void;

  /** Closes the dialog */
  abstract _onDialogClose(): void;

  /**
   * Attach a ComponentPortal as content to this dialog container.
   * @param portal Portal to be attached as the dialog content.
   */
  attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    return this._portalOutlet.attachComponentPortal(portal);
  }

  /**
   * Attach a TemplatePortal as content to this dialog container.
   * @param portal Portal to be attached as the dialog content.
   */
  attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
    return this._portalOutlet.attachTemplatePortal(portal);
  }
}

@Component({
  selector: 'arkik-dialog-container',
  template: `
    <div class="dialog-close-btn">
      <button
        type="button"
        app-icon-button
        size="24px"
        icon="close"
        color="blue"
        [title]="'views.globals.close' | translation"
        (click)="_onDialogClose()"
      ></button>
    </div>
    <ng-template cdkPortalOutlet></ng-template>
  `,
  styleUrls: ['./arkik-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
  animations: [dialogContainer],
  host: {
    class: 'arkik-dialog-container',
    '[@dialogContainer]': '_state',
    '(@dialogContainer.start)': '_onAnimationStart($event)',
    '(@dialogContainer.done)': '_onAnimationDone($event)',
  },
})
export class ArkikDialogContainerComponent extends _ArkikDialogContainerBase {
  /** State of the dialog animation. */
  _state: 'void' | 'enter' | 'exit' = 'enter';

  /** Callback, invoked whenever an animation on the host completes. */
  _onAnimationDone({ toState, totalTime }: AnimationEvent) {
    if (toState === 'enter') {
      this._animationStateChanged.next({ state: 'opened', totalTime });
    } else if (toState === 'exit') {
      this._animationStateChanged.next({ state: 'closed', totalTime });
    }
  }

  /** Callback, invoked when an animation on the host starts. */
  _onAnimationStart({ toState, totalTime }: AnimationEvent) {
    if (toState === 'enter') {
      this._animationStateChanged.next({ state: 'opening', totalTime });
    } else if (toState === 'exit' || toState === 'void') {
      this._animationStateChanged.next({ state: 'closing', totalTime });
    }
  }

  /** Starts the dialog exit animation. */
  _startExitAnimation(): void {
    this._state = 'exit';

    // Mark the container for check so it can react if the
    // view container is using OnPush change detection.
    this._changeDetectorRef.markForCheck();
  }

  _onDialogClose(): void {
    this._dialogClose.emit();
  }
}

interface ArkikAnimationEvent {
  state: 'opened' | 'opening' | 'closing' | 'closed';
  totalTime: number;
}

@Directive({
  selector: '[arkik-dialog-title], [arkikDialogTitle]',
  exportAs: 'arkikDialogTitle',
  host: {
    class: 'dialog-title',
  },
})
export class ArkikDialogTitle {}

@Directive({
  selector: '[arkik-dialog-content], arkik-dialog-content, [arkikDialogContent]',
  exportAs: 'arkikDialogContent',
  host: {
    class: 'dialog-content',
  },
})
export class ArkikDialogContent {}

@Directive({
  selector: '[arkik-dialog-actions], arkik-dialog-actions, [arkikDialogActions]',
  exportAs: 'arkikDialogActions',
  host: {
    class: 'dialog-actions',
  },
})
export class ArkikDialogActions {}
